import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Edit } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useGetVehiclesQuery } from "../vehiclesApi";
import { useCanCreateVehicles } from "../useVehiclePermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// Vehicle Master (handoff §20.1) - fleet roster only, mirrors the
// Employees master's architecture (own List+Form route pages, vehicleId is
// globally unique so no {branchId} composite key anywhere). Maintenance
// Requests (§20.3) is a separate, not-yet-built screen - out of scope here.
const VehiclesList = () => {
  const canCreate = useCanCreateVehicles();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: vehicles = [], isLoading } = useGetVehiclesQuery({
    search: search || undefined,
    includeInactive: status === "" || status === "inactive" ? true : undefined,
    branchId: selectedBranchId ?? undefined,
  });

  const filtered =
    status === "" ? vehicles : vehicles.filter((v) => Boolean(v.isActive) === (status === "active"));

  const columns = [
    {
      title: "Reg No",
      dataIndex: "regNo",
      width: 140,
      render: (value, record) => <Link to={paths.vehicleEdit(record.vehicleId)}>{value}</Link>,
    },
    { title: "Brand", dataIndex: "brand", render: (v) => v || "-" },
    { title: "Model", dataIndex: "model", render: (v) => v || "-" },
    { title: "Year", dataIndex: "makeYear", render: (v) => v || "-" },
    {
      title: "Allocated To",
      dataIndex: "allocatedToEmployeeName",
      render: (_, record) =>
        record.allocatedToEmployeeName || record.allocatedToDesignationName || "-",
    },
    { title: "Branch", dataIndex: "branchCode" },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <span className={`badge ${value ? "badge-linesuccess" : "badge-linedanger"}`}>
          {value ? "Active" : "Not In Use"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button
          type="button"
          className="btn btn-link p-2"
          title="Edit"
          onClick={() => navigate(paths.vehicleEdit(record.vehicleId))}
        >
          <Edit size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Vehicles</h4>
              <h6>Fleet roster and allocation</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.vehicleNew)}>
                <PlusCircle className="me-2" size={16} />
                Add New Vehicle
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search reg no, brand, or model"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={filtered} rowKey="vehicleId" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehiclesList;
